// features/auth/pages/WelcomePage.tsx
/**
 * Post-login picker.
 *
 * Users with BOTH staff capability and a player profile land here so
 * they can choose which dashboard to enter. Player-only users skip
 * this page entirely (PublicRoute / PlayerPublicRoute send them
 * straight to /player).
 *
 * Two storage layers:
 *  - localStorage (`roundmaster.welcomePreference`): the explicit
 *    "Remember my choice" pin. Survives across sessions until cleared.
 *  - sessionStorage (`roundmaster.welcomePreference.session`): the
 *    current-session pin set on every pick. Stops the picker from
 *    triggering on every navigation within one tab/session.
 *
 * EntryRedirect (used at /landing and /player) checks both and bumps
 * the user back to /welcome if neither is set.
 */

import { ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Stack,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import EngineeringIcon from '@mui/icons-material/Engineering';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';

import { PageContainer } from '../../../components/layout/PageContainer';
import { GlassCard, CardContent } from '../../../components/common/Card';
import { useAuth } from '../../../context/AuthContext';
import { ROUTES } from '../../../config/routes';

export const WELCOME_PREFERENCE_KEY = 'roundmaster.welcomePreference';
export const WELCOME_PREFERENCE_SESSION_KEY =
  'roundmaster.welcomePreference.session';
export type WelcomePreference = 'staff' | 'player';

/**
 * Reads the picker preference, preferring the persistent
 * (localStorage) pin over the session-only one. Returns null if
 * neither is set.
 */
export function readWelcomePreference(): WelcomePreference | null {
  try {
    const persistent = window.localStorage.getItem(WELCOME_PREFERENCE_KEY);
    if (persistent === 'staff' || persistent === 'player') return persistent;
  } catch {
    /* localStorage may be unavailable - fall through */
  }
  try {
    const session = window.sessionStorage.getItem(
      WELCOME_PREFERENCE_SESSION_KEY,
    );
    if (session === 'staff' || session === 'player') return session;
  } catch {
    /* sessionStorage may be unavailable - fall through */
  }
  return null;
}

export function WelcomePage(): ReactElement {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [remember, setRemember] = useState(true);

  const choose = (target: WelcomePreference) => {
    if (remember) {
      try {
        window.localStorage.setItem(WELCOME_PREFERENCE_KEY, target);
      } catch {
        // localStorage may be disabled - fall through, just navigate.
      }
    } else {
      try {
        window.localStorage.removeItem(WELCOME_PREFERENCE_KEY);
      } catch {
        /* ignore */
      }
    }
    // Always pin the choice for the rest of this session so EntryRedirect
    // doesn't bounce the user back to /welcome on the very next navigation
    // when they didn't tick "Remember my choice".
    try {
      window.sessionStorage.setItem(WELCOME_PREFERENCE_SESSION_KEY, target);
    } catch {
      /* ignore */
    }
    navigate(target === 'staff' ? ROUTES.LANDING : ROUTES.PLAYER_DASHBOARD);
  };

  const greeting = user?.firstName ? `, ${user.firstName}` : '';

  return (
    <PageContainer user={user} title="Welcome" titleGradient>
      <Box
        sx={{
          maxWidth: 720,
          mx: 'auto',
          textAlign: 'center',
          py: { xs: 2, md: 4 },
        }}
      >
        <Typography sx={{ color: '#FFF', fontSize: '1.4rem', mb: 1 }}>
          Welcome back{greeting}.
        </Typography>
        <Typography sx={{ color: '#8892A0', mb: 4 }}>
          Where would you like to go?
        </Typography>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <PickerCard
            icon={<EngineeringIcon sx={{ fontSize: 48, color: '#7B68EE' }} />}
            title="Staff Dashboard"
            description="Manage tournaments, run rounds, judge calls, deck check, and the rest of the staff toolkit."
            onClick={() => choose('staff')}
            color="#7B68EE"
          />
          <PickerCard
            icon={<SportsEsportsIcon sx={{ fontSize: 48, color: '#00FF88' }} />}
            title="Player Dashboard"
            description="See your tournament list, decks, results, and personal settings as a competitor."
            onClick={() => choose('player')}
            color="#00FF88"
          />
        </Stack>

        <FormControlLabel
          control={
            <Checkbox
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              sx={{ color: '#8892A0', '&.Mui-checked': { color: '#7B68EE' } }}
            />
          }
          label={
            <Typography sx={{ color: '#8892A0', fontSize: '0.85rem' }}>
              Remember my choice next time
            </Typography>
          }
        />
      </Box>
    </PageContainer>
  );
}

function PickerCard({
  icon,
  title,
  description,
  onClick,
  color,
}: {
  icon: ReactElement;
  title: string;
  description: string;
  onClick: () => void;
  color: string;
}): ReactElement {
  return (
    <GlassCard
      sx={{
        flex: 1,
        cursor: 'pointer',
        transition: 'transform 0.15s, border-color 0.15s',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        '&:hover': {
          transform: 'translateY(-2px)',
          borderColor: color,
        },
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ mb: 1 }}>{icon}</Box>
        <Typography
          variant="h6"
          sx={{ color: '#FFF', mb: 1, fontWeight: 600 }}
        >
          {title}
        </Typography>
        <Typography sx={{ color: '#8892A0', fontSize: '0.85rem', mb: 2 }}>
          {description}
        </Typography>
        <Button
          variant="contained"
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          sx={{
            bgcolor: color,
            color: color === '#00FF88' ? '#0A0E14' : '#FFF',
            '&:hover': { bgcolor: color, opacity: 0.85 },
          }}
        >
          Open
        </Button>
      </CardContent>
    </GlassCard>
  );
}

export default WelcomePage;
